import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import { Box } from "@mui/material";
import { type FC, useCallback, useEffect, useRef } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { OneTimeNoticeContent, useOneTimeNotice } from "../../UIToolkit/molecules/OneTimeNotice";
import { GAPS_WHATS_NEW_NOTICE } from "./gapsWhatsNewCampaign";

type Props = {
	departmentId: string;
};

/**
 * The one-time "what's new" note on the gaps-first home: this is a new layout, nothing else changed, and the
 * classic view is one tap away under More.
 *
 * The same campaign machinery as Management Center's "What's new", shown inline rather than behind an icon so
 * it is read on arrival instead of waiting to be found. The hook and the note are used separately (rather
 * than `OneTimeNotice`) because the eligibility also gates the `gaps_whats_new_shown` impression, which is
 * what makes "Got it" and "Back to classic view" measurable against how many people saw the note. The menu
 * labels are the menu's own keys, so the note can never name a row the More sheet does not have.
 */
export const GapsWhatsNewNotice: FC<Props> = ({ departmentId }) => {
	const { t } = useTranslation();
	const { track } = useAnalytics();
	const handleDismiss = useCallback(() => track("gaps_whats_new_dismissed", { department_id: departmentId }), [track, departmentId]);
	const notice = useOneTimeNotice(GAPS_WHATS_NEW_NOTICE.key, {
		expiresAt: GAPS_WHATS_NEW_NOTICE.expiresAt,
		// DEFAULT: no `onlyForAccountsCreatedBefore` — every account is told, new ones included; see
		// GAPS_WHATS_NEW_NOTICE for why.
		onDismiss: handleDismiss,
	});

	// Once per mount (the home remounts per department), not per render: `track`'s identity can turn over
	// when an analytics provider re-renders, which would otherwise count one view several times.
	const hasTrackedShownRef = useRef(false);
	useEffect(() => {
		if (!notice.isEligible || hasTrackedShownRef.current) return;
		hasTrackedShownRef.current = true;
		track("gaps_whats_new_shown", { department_id: departmentId });
	}, [notice.isEligible, track, departmentId]);

	return (
		<OneTimeNoticeContent
			isVisible={notice.isEligible}
			onAcknowledge={notice.dismiss}
			dismissLabel={t("common.gotIt")}
			tone="info"
			icon={<CampaignOutlinedIcon fontSize="inherit" aria-hidden />}
			actionPlacement="below"
			dataTestId="gaps-whats-new"
			sx={{ mx: 1.5, mt: 1, mb: 0.25 }}
		>
			<Trans
				i18nKey="gaps.whatsNew.message"
				values={{ more: t("gaps.nav.more"), classic: t("gaps.more.backToClassic") }}
				// The two labels are what the user has to find on screen, so they stand out from the sentence.
				components={{ b: <Box component="span" sx={{ fontWeight: 600 }} /> }}
			/>
		</OneTimeNoticeContent>
	);
};
